import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Alert,
  Spinner,
} from "react-bootstrap";
import { FaCheckCircle, FaCircle } from "react-icons/fa";
import UserTypeSelection from "./UserTypeSelection";
import UserDetailsForm from "./UserDetailsForm";
import PackageSelection from "../PackageSelection";
import { managerSignUp, coachSignUp } from "../../../backend/firebaseFunctions";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import "./SignUpProcess.css";

const SignUpProcess = ({
  onBackToLogin,
  initialUserType = null,
  initialPackage = null,
  onSignupSuccess = null,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Phase management
  const [currentPhase, setCurrentPhase] = useState(1);
  const [userType, setUserType] = useState(initialUserType || "");
  const [formData, setFormData] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(
    initialPackage || null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Total phases - 2 for coaches, 3 for managers
  const totalPhases = userType === "coach" ? 2 : 3;

  // Phase titles - different for coaches vs managers
  const phaseTitles =
    userType === "coach"
      ? ["בחירת סוג משתמש", "השלמת פרופיל"]
      : ["בחירת סוג משתמש", "השלמת פרופיל", "בחירת חבילה"];

  // Handle user type selection
  const handleUserTypeSelect = (type) => {
    if (type === "next") {
      if (userType) {
        setCurrentPhase(2);
        setError("");
      }
    } else {
      setUserType(type);
    }
  };

  // Handle user details completion
  const handleUserDetailsComplete = () => {
    if (userType === "coach") {
      // For coaches, complete registration directly after user details
      handleCoachRegistration();
    } else {
      // For managers, proceed to package selection
      setCurrentPhase(3);
      setError("");
    }
  };

  // Handle package selection
  const handlePackageSelection = (packageData) => {
    setSelectedPackage(packageData);
    setError("");
  };

  // Handle coach registration directly
  const handleCoachRegistration = async () => {
    setIsProcessing(true);
    setError("");
    try {
      // Validate required data for coach signup
      if (
        !formData.email ||
        !formData.password ||
        !formData.userName ||
        !formData.organizationCode ||
        !formData.userPhoneNumber
      ) {
        throw new Error("כל השדות נדרשים להרשמת מאמן");
      }

      await coachSignUp(
        formData.email,
        formData.password,
        formData.userName,
        formData.organizationCode,
        formData.userPhoneNumber
      );

      setSuccess(true);
      // The navigation will be handled by the useEffect when currentUser is available
    } catch (error) {
      console.error("Error during coach account creation:", error);
      setError(error.message || "אירעה שגיאה במהלך ההרשמה. אנא נסה שוב.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle package selection and complete signup
  const handlePackageSelectionComplete = async (packageData) => {
    setIsProcessing(true);
    setError("");
    try {
      // Validate required data for manager signup
      if (
        !formData.email ||
        !formData.password ||
        !formData.userName ||
        !formData.userOrganizationName ||
        !formData.userPhoneNumber ||
        !formData.userField ||
        !packageData
      ) {
        throw new Error("כל השדות נדרשים להרשמת מנהל");
      }

      await managerSignUp(
        formData.email,
        formData.password,
        formData.userName,
        formData.userOrganizationName,
        formData.userPhoneNumber,
        formData.userField,
        packageData
      );

      setSuccess(true);
      // The navigation will be handled by the useEffect when currentUser is available
    } catch (error) {
      console.error("Error during account creation:", error);
      setError(error.message || "אירעה שגיאה במהלך ההרשמה. אנא נסה שוב.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Go back to previous phase
  const handleGoBack = () => {
    if (currentPhase > 1) {
      setCurrentPhase(currentPhase - 1);
      setError("");
    } else {
      onBackToLogin();
    }
  };

  // Reset form data when going back
  useEffect(() => {
    // Only reset formData when going back to phase 1 or earlier
    if (currentPhase <= 1) {
      setFormData({});
    }
    // Only reset selectedPackage when going back to phase 2 or earlier
    if (currentPhase <= 2) {
      setSelectedPackage(null);
    }
  }, [currentPhase]);

  // Handle navigation when user is authenticated after successful signup
  useEffect(() => {
    if (success && currentUser) {
      // Let the parent component handle routing
      if (onSignupSuccess) {
        onSignupSuccess(currentUser);
      }
    }
  }, [success, currentUser, onSignupSuccess]);

  // Render current phase
  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case 1:
        return (
          <UserTypeSelection
            onUserTypeSelect={handleUserTypeSelect}
            userType={userType}
            onBack={handleGoBack}
          />
        );

      case 2:
        return (
          <UserDetailsForm
            userType={userType}
            onNext={handleUserDetailsComplete}
            onBack={handleGoBack}
            formData={formData}
            setFormData={setFormData}
            isProcessing={isProcessing}
          />
        );

      case 3:
        // Only show package selection for managers
        if (userType === "manager") {
          return (
            <PackageSelection
              onSelectPackage={handlePackageSelection}
              onCompleteRegistration={handlePackageSelectionComplete}
              selectedPackage={selectedPackage}
              isProcessing={isProcessing}
              onBack={handleGoBack}
            />
          );
        }
        return null;

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Container className="auth-container">
        <Card className="auth-card">
          <div className="text-center">
            <FaCheckCircle
              className="text-success"
              style={{ fontSize: "4rem", marginBottom: "1rem" }}
            />
            <h3 className="text-success mb-3">החשבון נוצר בהצלחה!</h3>
            <p className="text-muted mb-4">
              ברוכים הבאים ל-SimplyCoach! החשבון שלך נוצר בהצלחה ואתה מועבר ללוח
              הבקרה שלך.
            </p>
            <Spinner animation="border" variant="primary" />
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="auth-container">
      <Card className="auth-card">
        {/* Progress Header */}
        <div className="text-center mb-4">
          <h3 className="mb-3">צור את החשבון שלך</h3>

          {/* Progress Bar */}
          <ProgressBar
            now={(currentPhase / totalPhases) * 100}
            className="mb-3"
            style={{ height: "8px", borderRadius: "4px" }}
          />

          {/* Phase Indicators */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            {phaseTitles.map((title, index) => (
              <div
                key={index}
                className="d-flex flex-column align-items-center"
              >
                <div
                  className={`phase-indicator ${
                    currentPhase > index + 1
                      ? "completed"
                      : currentPhase === index + 1
                      ? "current"
                      : ""
                  }`}
                >
                  {currentPhase > index + 1 ? (
                    <FaCheckCircle className="text-success" />
                  ) : (
                    <FaCircle />
                  )}
                </div>
                <small
                  className={`phase-title ${
                    currentPhase > index + 1
                      ? "text-success"
                      : currentPhase === index + 1
                      ? "text-primary"
                      : "text-muted"
                  }`}
                >
                  {title}
                </small>
              </div>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Alert
            variant="danger"
            className="mb-4"
            onClose={() => setError("")}
            dismissible
          >
            {error}
          </Alert>
        )}

        {/* Current Phase Content */}
        {renderCurrentPhase()}
      </Card>
    </Container>
  );
};

export default SignUpProcess;
